import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import Swal from 'sweetalert2'
import Select from 'react-select'
import { postRequestOptions } from '../helpers/Fetchwrapper'
import Spinner from 'react-bootstrap/Spinner'

interface PackingBomOption {
	label: string;
	value: number;
}

interface PackingBomCreateModalProps {
	show: boolean;
	handleClose: (saved?: boolean) => void;
	product: any;
	onCreated?: (bomId: number, bomName: string) => void;
	defaultBomName?: string;
	existingBoms?: PackingBomOption[];
}

const PackingBomCreateModal: React.FC<PackingBomCreateModalProps> = ({
	show,
	handleClose,
	product,
	onCreated,
	defaultBomName = '',
	existingBoms = [],
}) => {

	const history = useHistory();
	const [bomName, setBomName] = useState('');
	const [remarks, setRemarks] = useState('');
	const [copyFromBom, setCopyFromBom] = useState<PackingBomOption | null>(null);
	const [loading, setLoading] = useState(false);

	const showCopyBom = existingBoms.length > 0;

	useEffect(() => {
		if (show) {
			setBomName(defaultBomName);
			setRemarks('');
			setCopyFromBom(null);
		}
	}, [show, product, defaultBomName]);

	const handleSave = async () => {
		const trimmedName = bomName.trim();
		if (!trimmedName) {
			Swal.fire({ icon: 'warning', title: 'Validation', text: 'BOM name is required.' });
			return;
		}

		setLoading(true);
		try {
			const payload: Record<string, unknown> = {
				bomName: trimmedName,
				remarks: remarks.trim(),
			};

			if (copyFromBom?.value) {
				payload.copyFromBomId = copyFromBom.value;
			}

			const response = await fetch(
				`${process.env.REACT_APP_API_URL}packing-boms/${product.idProducts}`,
				postRequestOptions(payload)
			);
			const respData = await response.json();
			if (respData.success) {
				const newBomId = respData.data?.id_packing_boms;
				handleClose(true);
				if (onCreated) {
					onCreated(newBomId, trimmedName);
				} else {
					history.push(`/packing-bom-formulation/${product.idProducts}?id_packing_bom=${newBomId}`);
				}
			} else {
				Swal.fire({ icon: 'error', title: 'Error', html: respData.errorMessage || 'Could not create packing BOM.' });
			}
		} catch (error) {
			console.log(error);
			Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong.' });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal show={show} onHide={() => handleClose(false)} size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>Create BOM {product?.productName ? `- ${product.productName}` : ''}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form.Group className="mb-3">
					<Form.Label>BOM Name</Form.Label>
					<Form.Control
						type="text"
						value={bomName}
						onChange={(e) => setBomName(e.target.value)}
						placeholder="Enter BOM name"
					/>
				</Form.Group>
				<Form.Group className="mb-3">
					<Form.Label>Remarks</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						value={remarks}
						onChange={(e) => setRemarks(e.target.value)}
						placeholder="Enter remarks"
					/>
				</Form.Group>
				{showCopyBom && (
					<Form.Group className="mb-3">
						<Form.Label>Copy BOM</Form.Label>
						<Select
							value={copyFromBom}
							onChange={(option) => setCopyFromBom(option)}
							options={existingBoms}
							placeholder="Select BOM to copy"
							isClearable
						/>
					</Form.Group>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="sm" onClick={() => handleClose(false)}>
					Close
				</Button>
				<Button variant="primary" size="sm" onClick={handleSave} disabled={loading}>
					{loading && <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />}
					<span> Submit</span>
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default PackingBomCreateModal
